import { all, call, put, take, fork, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import _ from 'lodash';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { toaster } from 'src/helpers/UtilityHelper';
import * as types from 'src/state/ducks/notification/types';
import * as actions from 'src/state/ducks/notification/actions';
import * as orderActions from 'src/state/ducks/order/actions';
import * as userActions from 'src/state/ducks/user/actions';
import * as api from 'src/state/ducks/notification/api';
import * as orderApi from 'src/state/ducks/order/api';
import * as userApi from 'src/state/ducks/user/api';
import * as bakedBreadApi from 'src/state/ducks/bakedBread/api';
import * as bakedBreadActions from 'src/state/ducks/bakedBread/actions';
import { State } from 'src/state/ducks';
import { Status } from 'src/constants/Interface';
import { APISuccessfulResponse, APIFailedResponse } from 'src/firebase/db/DatabaseAdapter';
import ValidationManager from 'src/firebase/db/ValidationManager';
import { NotificationEntity, notificationInitialEntity, notificationEntityKeys } from 'src/firebase/db/Notifications';
import { OrderBreadEntity, orderBreadInitialEntity ,orderBreadEntityKeys } from 'src/firebase/db/OrderBreads';
import { BakedBreadEntity, bakedBreadInitialEntity, bakedBreadEntityKeys } from 'src/firebase/db/BakedBreads';
import { UserRole } from 'src/firebase/db/Admins';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

function* validateNotification() {
  while (true) {
    const { payload } = yield take(types.VALIDATE_NOTIFICATION);

    // 入力された値を検証する
    const form = payload as NotificationEntity;
    const validator = new NotificationValidationManager();

    // 入力必須
    const IS_REQUIRED = ['scheduled_time', 'last_pickup_time', 'can_order_time'];

    notificationEntityKeys.forEach((key) => {
      if (IS_REQUIRED.includes(key)) {
        validator.isRequired(key, form[key]);
      }

      const times = {
        scheduledTime: form['scheduled_time'],
        canOrderTime: form['can_order_time'],
        lastPickupTime: form['last_pickup_time'],
      };

      switch (key) {
        case 'template_title': {
          if (form['needs_template'] && form['template_title'] === '') {
            validator.isRequired('template_title', form['template_title']);
          }
          break;
        }
        case 'scheduled_time': {
          validator.isValidScheduledTime(form['scheduled_time']);
          break;
        }
        case 'order_breads': {
          validator.isValidOrderBreads(form['order_breads']);
          break;
        }
        case 'can_order_time': {
          validator.isValidCanOrderTime(times);
          break;
        }
        case 'last_pickup_time': {
          validator.isValidLastPickupTime(times);
          break;
        }
      }
    });

    if (validator.isPass()) {
      yield put(actions.successValidateNotification(payload));
    } else {
      const errors = validator.throwValidateError(validator.getErrors());
      yield put(actions.failValidateNotification(errors));
    }
  }
}

function* validateReservation() {
  while (true) {
    const { payload } = yield take(types.VALIDATE_RESERVATION);

    // 入力された値を検証する
    const form = payload as {
      bakedBread: BakedBreadEntity;
      orderBread: OrderBreadEntity;
      notification: NotificationEntity;
    };
    const { bakedBread, orderBread, notification } = form;
    const validator = new ReservationValidationManager();

    const BAKED_BREAD_IS_REQUIRED = [
      'shop_id',
      'name',
      'image',
      'price',
      'description',
      'best_before',
    ];
    bakedBreadEntityKeys.forEach((key) => {
      if (BAKED_BREAD_IS_REQUIRED.includes(key)) {
        validator.isRequired(key, bakedBread[key]);
      }
    });

    const ORDER_BREAD_IS_REQUIRED = ['stock', 'maximum_order'];
    orderBreadEntityKeys.forEach((key) => {
      if (ORDER_BREAD_IS_REQUIRED.includes(key)) {
        validator.isRequired(key, orderBread[key]);
      }
    });

    const NOTIFICATION_IS_REQUIRED = ['published_time', 'scheduled_time', 'can_order_time'];
    notificationEntityKeys.forEach((key) => {
      if (NOTIFICATION_IS_REQUIRED.includes(key)) {
        validator.isRequired(key, notification[key]);
      }

      switch (key) {
        case 'last_pickup_days': {
          validator.isValidLastPickupDays(notification[key]);
          break;
        }
      }
    });

    if (validator.isPass()) {
      yield put(actions.successValidateReservation(payload));
    } else {
      toaster('error', '入力エラーがあります。');
      const errors = validator.throwValidateError(validator.getErrors());
      console.log({ errors });
      yield put(actions.failValidateReservation(errors));
    }
  }
}

function* getNotifications() {
  while (true) {
    const { payload } = yield take(types.GET_NOTIFICATIONS);
    try {
      const res = yield call(api.getNotifications, payload.shopId);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetNotifications(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failGetNotifications(res));
      }
    } catch (err) {
      yield put(
        actions.failGetNotifications({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* getReservations() {
  while (true) {
    const { payload } = yield take(types.GET_RESERVATIONS);
    try {
      const state: State = yield select();
      const isAdmin = state.admin.user.role === UserRole.ADMIN && !state.admin.user.shop_ids.length;
      const res = yield call(api.getReservations, {
        ...payload,
        isAdmin,
      });
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetReservations(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failGetReservations(res));
      }
    } catch (err) {
      console.error(err);
      yield put(
        actions.failGetReservations({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* hasNotifications() {
  while (true) {
    const { payload } = yield take(types.HAS_NOTIFICATIONS);
    try {
      const res = yield call(api.hasNotifications, payload.shopId);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successHasNotifications(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failHasNotifications(res));
      }
    } catch (err) {
      console.error(err);
      yield put(
        actions.failHasNotifications({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* getReservation() {
  while (true) {
    const { payload } = yield take(types.GET_RESERVATION);
    try {
      const res = yield call(api.getReservation, payload.id);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetReservation(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failGetReservation(res));
      }
    } catch (err) {
      console.error(err);
      yield put(
        actions.failGetReservation({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* getDayReservation() {
  while (true) {
    const { payload } = yield take(types.GET_DAY_RESERVATION);
    try {
      let state: State = yield select();
      const isAdmin = state.admin.user.role === UserRole.ADMIN && !state.admin.user.shop_ids.length;
      const shopId = state.admin.login_shop_id;

      // 予約データを取得
      const res = yield call(api.getDayReservation, {
        ...payload,
        shopId,
        isAdmin,
      });
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetDayReservation(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failGetDayReservation(res));
      }

      // 当日の注文データからユーザー別の注文履歴も取得する
      state = yield select();
      const reservation = state.notification.reservation.resource;
      const user_ids = _.uniq(reservation.orders.map(({ user_id }) => user_id));
      const usersOrdersRes = yield call(orderApi.getUsersOrders, user_ids);
      if (usersOrdersRes.status === Status.SUCCESS) {
        yield put(orderActions.successGetUsersOrders(usersOrdersRes));
      } else if (usersOrdersRes.status === Status.INVALID) {
        yield put(orderActions.failGetUsersOrders(usersOrdersRes));
      }

      // ユーザー情報を取得
      const usersRes = yield call(userApi.getUsers, user_ids);
      if (usersRes.status === Status.SUCCESS) {
        yield put(userActions.successGetUsers(usersRes));
      } else if (usersRes.status === Status.INVALID) {
        yield put(userActions.failGetUsers(usersRes));
      }
    } catch (err) {
      console.error(err);
      yield put(
        actions.failGetDayReservation({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* getDayNotifications() {
  while (true) {
    const { payload } = yield take(types.GET_DAY_NOTIFICATIONS);
    try {
      const res = yield call(
        api.getDayNotifications,
        payload.shopId,
        payload.startDate,
        payload.endDate,
        payload.isReservation,
        payload.noDetails,
      );
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetDayNotifications(res));
      }
    } catch (err) {
      console.error(err);
      yield put(
        actions.failGetDayNotifications({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* getTemplatesNotifications() {
  while (true) {
    const { payload } = yield take(types.GET_TEMPLATES_NOTIFICATIONS);
    try {
      const res = yield call(api.getTemplatesNotifications, payload);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetTemplatesNotifications(res));
      } else {
        yield put(actions.failGetTemplatesNotifications(res));
      }
    } catch (err) {
      console.error(err);
      yield put(
        actions.failGetTemplatesNotifications({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* createNotification() {
  while (true) {
    const { payload } = yield take(types.CREATE_NOTIFICATION);
    try {
      const res = yield call(api.createNotification, payload);
      console.log({ res });
      if (res.status === Status.SUCCESS) {
        yield put(actions.successCreateNotification(res));
        yield put(push('/notifications/complete'));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failCreateNotification(res));
      }
    } catch (err) {
      console.error(err);
      yield put(
        actions.failCreateNotification({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* createNotifications() {
  while (true) {
    const { payload } = yield take(types.CREATE_NOTIFICATIONS);
    const notifications = payload as NotificationEntity[];
    try {
      const res = yield all(
        notifications.map((notification) => call(api.createNotification, notification)),
      ) || [];
      const checkResponseStatus = (status: number) =>
        res.every(
          (v: APISuccessfulResponse<NotificationEntity[]> | APIFailedResponse) =>
            v.status === status,
        );
      if (checkResponseStatus(Status.SUCCESS)) {
        yield put(actions.successCreateNotification(res));
        yield put(push('/notifications/complete'));
      } else if (checkResponseStatus(Status.INVALID)) {
        yield put(actions.failCreateNotification(res));
      }
    } catch (err) {
      console.log({ err });
      yield put(
        actions.failCreateNotification({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* createReservation() {
  while (true) {
    const { payload } = yield take(types.CREATE_RESERVATION);
    console.log({ payload })
    try {
      const { notification } = payload;

      // バリデーションを無視して作成させる
      const bakedBreadRes = yield call(bakedBreadApi.createBakedBread, {
        ...payload.bakedBread,
        is_reservation: true,
        is_draft: notification.is_draft,
      });

      if (bakedBreadRes.status === Status.SUCCESS) {
        yield put(bakedBreadActions.successCreateBakedBread(bakedBreadRes));
      } else {
        yield put(bakedBreadActions.failCreateBakedBread(bakedBreadRes));
        yield put(
          actions.failCreateNotification({
            status: Status.FAILURE,
            error: {
              code: 500,
              message: 'Failed at failCreateBakedBread...',
            },
          }),
        );
        return;
      }

      const {
        data: { id: bread_id, image },
      } = bakedBreadRes;
      const notificationRes = yield call(api.createReservation, {
        orderBread: {
          ...payload.orderBread,
          bread_id,
          image,
        },
        notification: payload.notification,
      });

      if (notificationRes.status === Status.SUCCESS) {
        yield put(actions.successCreateReservation(notificationRes));
        const {
          data: { is_draft },
        } = notificationRes;
        yield put(push(`/reservations${is_draft ? '' : '/complete'}`));
        toaster('success', is_draft ? '下書き保存しました。' : '配信予約しました。');
      } else {
        yield put(actions.failCreateReservation(notificationRes));
      }
    } catch (err) {
      console.error(err);
      yield put(
        actions.failCreateNotification({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* updateReservation() {
  while (true) {
    const { payload } = yield take(types.UPDATE_RESERVATION);
    try {

      // バリデーションを無視して更新させる
      const bakedBreadRes = yield call(bakedBreadApi.updateBakedBread, {
        ...payload.bakedBread,
        is_draft: true,
      });

      if (bakedBreadRes.status === Status.SUCCESS) {
        yield put(bakedBreadActions.successUpdateBakedBread(bakedBreadRes));
      } else {
        yield put(bakedBreadActions.failUpdateBakedBread(bakedBreadRes));
        return;
      }

      const {
        data: { image },
      } = bakedBreadRes;
      const notificationRes = yield call(api.updateReservation, {
        orderBread: {
          ...payload.orderBread,
          image,
        },
        notification: payload.notification,
      });

      if (notificationRes.status === Status.SUCCESS) {
        const { shop_id: shopId, is_draft } = payload.notification;
        yield put(actions.successUpdateReservation(notificationRes));
        yield put(push(`/reservations${is_draft ? '' : '/complete'}`));
        toaster('success', is_draft ? '下書き保存しました。' : '配信予約しました。');
        yield put(actions.getReservations({ shopId }));
      } else {
        yield put(actions.failUpdateReservation(notificationRes));
      }
    } catch (err) {
      console.error(err);
      yield put(
        actions.failUpdateReservation({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* deleteReservation() {
  while (true) {
    const { payload } = yield take(types.DELETE_RESERVATION);
    try {
      const res = yield call(api.deleteReservation, payload);

      if (res.status === Status.SUCCESS) {
        yield put(actions.successDeleteReservation(res));
        const {
          data: { shop_id: shopId },
        } = res;
        yield put(actions.getReservations({ shopId }));
        toaster('success', '削除しました。');
      } else {
        yield put(actions.failUpdateReservation(res));
      }
    } catch (err) {
      console.error(err);
      yield put(
        actions.failUpdateReservation({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* duplicateReservation(payload: {
  bakedBread: BakedBreadEntity;
  orderBread: OrderBreadEntity;
  notification: NotificationEntity;
}) {
  try {
    const bakedBreadRes = yield call(bakedBreadApi.createBakedBread, {
      ...payload.bakedBread,
      is_reservation: true,
      is_draft: true,
    });
    if (bakedBreadRes.status === Status.SUCCESS) {
      yield put(bakedBreadActions.successCreateBakedBread(bakedBreadRes));
    } else {
      yield put(bakedBreadActions.failCreateBakedBread(bakedBreadRes));
      yield put(
        actions.failCreateNotification({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: 'Failed at failCreateBakedBread...',
          },
        }),
      );
      return;
    }
    const {
      data: { id: bread_id },
    } = bakedBreadRes;
    const notificationRes = yield call(api.createReservation, {
      orderBread: {
        ...payload.orderBread,
        bread_id,
      },
      notification: payload.notification,
    });
    if (notificationRes.status === Status.SUCCESS) {
      yield put(actions.successCreateReservation(notificationRes));
    } else {
      yield put(actions.failCreateReservation(notificationRes));
    }
  } catch (err) {
    console.error(err);
    yield put(
      actions.failCreateNotification({
        status: Status.FAILURE,
        error: {
          code: 500,
          message: err.message,
        },
      }),
    );
  }
}

function* duplicateReservations() {

    const { payload } = yield take(types.DUPLICATE_RESERVATIONS);
    const ids = payload as string[];
    try {

      // 選択されたIDの元データを複数取得
      const resGetReservations = yield all(
        ids.map((id) => call(api.getReservation, id)),
      ) || [];
    
      type ReservationProps = {
        bakedBread: BakedBreadEntity;
        orderBread: OrderBreadEntity;
        notification: NotificationEntity;
      }

      // レスポンスチェック
      const checkReservationResponseStatus = (status: number) =>
      resGetReservations.every(
          (v: APISuccessfulResponse<ReservationProps> | APIFailedResponse) =>
            v.status === status,
        );
      if (checkReservationResponseStatus(Status.SUCCESS)) {

        // コピー元のデータを整形する
        const reservations = resGetReservations.map((v: APISuccessfulResponse<ReservationProps>) => {
          const { data: { bakedBread, orderBread, notification } } = v;

          // 不要なフィールドを生成しないように、それぞれ値を明示的に設定
          return {
            notification: {
              ...notificationInitialEntity,
              id: '',
              shop_id: notification.shop_id,
              shop_name: notification.shop_name,
              shop_address: notification.shop_address,
              published_time: notification.published_time,
              scheduled_time: notification.scheduled_time,
              can_order_time: notification.can_order_time,
              last_pickup_time: notification.last_pickup_time,
              last_pickup_days: notification.last_pickup_days,
              pickup_supplement: notification.pickup_supplement,
              is_draft: true,
              is_published: false,
              is_notified: false,
              is_reservation: true,
              notified_at: undefined,
            },
            orderBread: {
              ...orderBreadInitialEntity,
              shop_id: orderBread.shop_id,
              shop_name: orderBread.shop_name,
              shop_address: orderBread.shop_address,
              image: orderBread.image,
              name: `【コピー】${orderBread.name}`,
              description: orderBread.description,
              can_order_time: orderBread.can_order_time,
              last_pickup_time: orderBread.last_pickup_time,
              price: orderBread.price,
              stock: orderBread.stock,
              maximum_order: orderBread.maximum_order,
              pickup_supplement: orderBread.pickup_supplement,
              width: orderBread.width,
              depth: orderBread.depth,
              height: orderBread.height,
              best_before: orderBread.best_before,
              is_reservation: true,
              is_notified: false,
            },
            bakedBread: {
              ...bakedBreadInitialEntity,
              id: '',
              name: `【コピー】${bakedBread.name}`,
              image: bakedBread.image,
              price: bakedBread.price,
              description: bakedBread.description,
              width: bakedBread.width,
              depth: bakedBread.depth,
              height: bakedBread.height,
              best_before: bakedBread.best_before,
              is_reservation: true,
              is_draft: true,
              shop_id: orderBread.shop_id, // bakedBreadのshop_refから取得すると処理が煩雑になるので、orderBreadから取得
              baked_bread_tags: bakedBread.baked_bread_tags,
            },
          }
        }) as ReservationProps[];

        // コピーデータで新規作成
        const resCreateReservation = yield all(
          reservations.map((v) => call(duplicateReservation, v))
        );
        
        yield put(actions.successDuplicateReservations(resCreateReservation));
        toaster('success', '複製しました。');

        // 一覧表示の再取得
        const state: State = yield select();
        const shopId = state.admin.login_shop_id;
        yield put(actions.getReservations({ shopId }));

      } else {
        yield put(actions.failDuplicateReservations(resGetReservations));
      }
    } catch (err) {
      console.log({ err });
      yield put(
        actions.failDuplicateReservations({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
}

function* cancelReservation() {
  while (true) {
    const { payload } = yield take(types.CANCEL_RESERVATION);
    try {
      const res = yield call(api.cancelReservation, payload);
      const { status } = res;
      if (status === Status.SUCCESS) {
        yield put(actions.successCancelReservation(res));
        const currentUrl = window.location.href;
        const lastURLSegment = currentUrl.substr(currentUrl.lastIndexOf('/') + 1);
        if (lastURLSegment === 'history') {
          // 一覧ページの場合は再描画する
          const state: State = yield select();
          const shopId = state.admin.login_shop_id;
          yield put(actions.getReservations({ shopId }));
        }
      } else {
        yield put(actions.failCancelReservation(res));
      }
    } catch (err) {
      console.log({ err });
      toaster('error', err.message);
      yield put(
        actions.failCancelReservation({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* removeOrderBreadFromNotification() {
  while (true) {
    const { payload } = yield take(types.REMOVE_ORDER_BREAD_FROM_NOTIFICATION);
    try {
      const res = yield call(api.removeOrderBreadFromNotification, payload);
      const { status, data: orderBread } = res;
      if (status === Status.SUCCESS) {
        yield put(actions.successRemoveOrderBreadFromNotification(res));
        const currentUrl = window.location.href;
        const lastURLSegment = currentUrl.substr(currentUrl.lastIndexOf('/') + 1);

        if (dayjs(lastURLSegment, 'YYYY-MM-DD').isValid()) {
          // 一覧ページの場合は再描画する
          const date = dayjs(lastURLSegment);
          console.log(date.toDate());
          yield put(
            actions.getDayNotifications({
              shopId: orderBread.shop_id,
              startDate: date.startOf('date').toDate(),
              endDate: date.endOf('date').toDate(),
              isReservation: false,
            }),
          );
        }
      } else {
        yield put(actions.failRemoveOrderBreadFromNotification(res));
      }
    } catch (err) {
      console.log({ err });
      yield put(
        actions.failRemoveOrderBreadFromNotification({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

const template = [
  getNotifications(),
  hasNotifications(),
  getReservation(),
  getReservations(),
  getDayReservation(),
  getTemplatesNotifications(),
  validateNotification(),
  validateReservation(),
  createNotification(),
  createNotifications(),
  createReservation(),
  updateReservation(),
  cancelReservation(),
  deleteReservation(),
  duplicateReservations(),
  removeOrderBreadFromNotification(),
  fork(getDayNotifications),
];

class NotificationValidationManager extends ValidationManager {
  isValidScheduledTime(scheduledTime: Date | undefined) {
    if (!scheduledTime) return;

    const now = new Date();
    const bakedAt = dayjs(scheduledTime);

    const key = 'scheduled_time';
    if (bakedAt.isSameOrBefore(now)) {
      const msg = '焼き上がりのお知らせ時刻は、現在より後の時刻を入力してください。';
      this.addErrors(key, msg);
      this.addErrors(`${key}_top`, msg);
    } else if (bakedAt.isBefore(dayjs(now).add(15, 'minute'))) {
      // this.addErrors(
      //   key,
      //   "お知らせ時刻は少なくとも15分以上あとの時刻で設定してください。"
      // );
    }
  }

  isValidOrderBreads(orderBreads: OrderBreadEntity[]) {
    const key = 'order_breads';
    const selectedOrderBreads = orderBreads.filter((orderBread) => orderBread.selected);

    if (selectedOrderBreads.length === 0) {
      this.addErrors(key, 'パンを少なくとも1つ以上選択してください。');
      return;
    }

    const validation = this;
    selectedOrderBreads.forEach((orderBread) => {
      const { bread_id, stock, maximum_order } = orderBread;
      const target = `${key}|${bread_id}`;
      if (stock < 1) {
        validation.addErrors(target, '販売個数を少なくとも1つ以上設定してください。');
      }
      if (maximum_order < 1) {
        validation.addErrors(target, '購入上限数を少なくとも1つ以上設定してください。');
      }
    });
  }

  isValidCanOrderTime(params: {
    scheduledTime: Date | undefined;
    canOrderTime: Date | undefined;
    lastPickupTime: Date | undefined;
  }) {
    const { scheduledTime, canOrderTime, lastPickupTime } = params;

    if (!canOrderTime) return;

    const now = dayjs(new Date());
    const bakedAt = dayjs(scheduledTime);
    const canOrderBy = dayjs(canOrderTime);
    const lastPickupAt = dayjs(lastPickupTime);
    const key = 'can_order_time';
    if (canOrderBy.isSameOrBefore(now)) {
      this.addErrors(key, 'ラストオーダー時刻が、現在以前の時刻になっています。');
    }
    if (scheduledTime && canOrderBy.isSameOrBefore(bakedAt)) {
      this.addErrors(key, 'ラストオーダー時刻が、焼き上がりの時刻以前になっています。');
    } else if (lastPickupTime && canOrderBy.isSameOrAfter(lastPickupAt)) {
      this.addErrors(key, 'ラストオーダー時刻が、パンを受け取れる最終時刻以降になっています。');
    }
  }

  isValidLastPickupTime(params: {
    scheduledTime: Date | undefined;
    canOrderTime: Date | undefined;
    lastPickupTime: Date | undefined;
  }) {
    const { scheduledTime, canOrderTime, lastPickupTime } = params;

    if (!lastPickupTime || !scheduledTime || !canOrderTime) return;

    const now = dayjs(new Date());
    const bakedAt = dayjs(scheduledTime);
    const canOrderBy = dayjs(canOrderTime);
    const lastPickupAt = dayjs(lastPickupTime);
    const key = 'last_pickup_time';
    if (lastPickupAt.isSameOrBefore(now)) {
      this.addErrors(key, 'パンを受け取れる最終時刻が、現在以前の時刻になっています。');
    }
    if (lastPickupAt.isSameOrBefore(bakedAt)) {
      this.addErrors(key, 'パンを受け取れる最終時刻が、焼き上がりの時刻以前になっています。');
    } else if (lastPickupAt.isSameOrBefore(canOrderBy)) {
      this.addErrors(key, 'パンを受け取れる最終時刻が、ラストオーダー時刻以前になっています。');
    }
  }
}

class ReservationValidationManager extends ValidationManager {
  isValidLastPickupDays(lastPickupDays: (Date | undefined)[]) {
    const key = 'last_pickup_days';
    if (!lastPickupDays.every((v) => v)) {
      this.addErrors(key, '受け渡し日が不正です。');
    }
    if (!lastPickupDays.length) {
      this.addErrors(key, '入力必須です');
    }
  }
}

export default template;
